import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { getApiBaseUrl } from '../lib/utils';

function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    email: searchParams.get('email') || '',
    name: '',
    role: 'tenant',
  });
  const [condo, setCondo] = useState(null);
  const [unitOptions, setUnitOptions] = useState([]);
  const [unitSearch, setUnitSearch] = useState('');
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingCondo, setLoadingCondo] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const fetchCondo = async () => {
      try {
        const response = await axios.get(`${getApiBaseUrl()}/api/condos`);
        const condos = response.data.condos || [];
        if (condos.length > 0) {
          const c = condos[0];
          setCondo(c);
          const unitsRes = await axios.get(`${getApiBaseUrl()}/api/condos/${c._id}/units`);
          setUnitOptions(unitsRes.data.units || []);
        }
      } catch (err) {
        console.error('Failed to fetch condos:', err);
      } finally {
        setLoadingCondo(false);
      }
    };
    fetchCondo();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'role' && value === 'manager') {
      setSelectedUnit(null);
      setUnitSearch('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (formData.role !== 'manager' && selectedUnit === null) {
      setError('Please select a unit');
      return;
    }

    setLoading(true);

    const result = await register({
      email: formData.email,
      name: formData.name,
      role: formData.role,
      unit: formData.role !== 'manager' ? String(selectedUnit) : undefined,
      condoId: condo?._id
    });

    if (result.success) {
      setSuccessMessage(result.message || 'Registration successful');
      if (result.codeSent) {
        navigate('/login', { state: { email: result.email, codeSent: true, message: result.message } });
        return;
      }
      if (result.loggedIn) {
        const loggedInUser = result.user;
        if (loggedInUser?.condos && loggedInUser.condos.length > 0) {
          const condo = loggedInUser.condos[0];
          navigate(`/${condo.slug || condo}`, { state: { successMessage: result.message } });
        } else {
          navigate('/', { state: { successMessage: result.message } });
        }
        return;
      }
      setSuccess(true);
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-card rounded-lg shadow-xl p-8 border border-border text-center">
          <div className="text-6xl mb-4">✅</div>
          <h1 className="text-3xl font-bold text-foreground mb-4">
            Registration Successful!
          </h1>
          <p className="text-muted-foreground mb-6">
            {successMessage || 'Your account has been created and is pending approval. An administrator will review and activate your account shortly.'}
          </p>
          <Link
            to="/login"
            className="inline-block bg-primary text-primary-foreground px-6 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full bg-card rounded-lg shadow-xl p-8 border border-border">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Create Account
          </h1>
          <p className="text-muted-foreground">
            Register to access your condo community
          </p>
        </div>

        {loadingCondo ? (
          <div className="text-center py-4">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : condo ? (
          <div className="bg-secondary/50 rounded-lg p-4 mb-6 border border-border">
            <p className="text-sm text-muted-foreground mb-1">Registering for:</p>
            <p className="font-semibold text-foreground">{condo.name}</p>
            <p className="text-sm text-muted-foreground">{condo.address}</p>
          </div>
        ) : (
          <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-4 py-3 rounded mb-6">
            No condos available. Please contact an administrator.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5" autoComplete="on">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground"
              placeholder="you@example.com"
              autoComplete="email"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
              Full Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground"
              placeholder="John Doe"
              autoComplete="name"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label htmlFor="role" className="block text-sm font-medium text-foreground mb-2">
              Your Role
            </label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground"
              disabled={loading}
            >
              <option value="tenant">Tenant</option>
              <option value="owner">Owner</option>
              <option value="council">Council Member</option>
              <option value="manager">Manager</option>
            </select>
          </div>

          {formData.role !== 'manager' && (
            <div className="relative">
              <label className="block text-sm font-medium text-foreground mb-2">
                Unit Number
              </label>
              {selectedUnit !== null ? (
                <div className="flex items-center gap-2 px-4 py-3 bg-background border border-border rounded-lg">
                  <span className="text-foreground flex-1">Unit {selectedUnit}</span>
                  <button
                    type="button"
                    onClick={() => { setSelectedUnit(null); setUnitSearch(''); }}
                    className="text-muted-foreground hover:text-destructive leading-none"
                    disabled={loading}
                  >
                    &times;
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={unitSearch}
                    onChange={(e) => setUnitSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const matches = unitOptions.filter(n => String(n).includes(unitSearch));
                        if (matches.length === 1) {
                          e.preventDefault();
                          setSelectedUnit(matches[0]);
                          setUnitSearch('');
                        }
                      }
                    }}
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground"
                    placeholder="Search unit number..."
                    disabled={loading}
                  />
                  {unitSearch.length > 0 && (() => {
                    const matches = unitOptions.filter(n => String(n).includes(unitSearch));
                    return (
                      <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {matches.length > 0 ? matches.map(num => (
                          <button
                            key={num}
                            type="button"
                            onClick={() => { setSelectedUnit(num); setUnitSearch(''); }}
                            className="w-full px-4 py-2.5 text-left text-sm text-foreground hover:bg-muted/50 transition-colors border-b border-border last:border-b-0"
                          >
                            Unit {num}
                          </button>
                        )) : (
                          <div className="px-4 py-2.5 text-sm text-muted-foreground">No matching units</div>
                        )}
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          )}

          {error && (
            <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-4 py-3 rounded">
              {error}
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            By creating an account, you agree to our{' '}
            <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
          </p>

          <button
            type="submit"
            disabled={loading || !condo}
            className="w-full bg-primary text-primary-foreground py-3 rounded-lg font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div className="mt-6 text-center space-y-4">
          <p className="text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:underline font-medium">
              Sign in
            </Link>
          </p>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground block">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
